import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

const fontFamilies = [
  { value: 'system', label: 'ስርዓት ፊደል', family: 'system-ui, -apple-system, sans-serif' },
  { value: 'nyala', label: 'ኛላ', family: 'Nyala, serif' },
  { value: 'abyssinica', label: 'አብሲኒካ', family: 'Abyssinica SIL, serif' },
  { value: 'jiret', label: 'ጅረት', family: 'Jiret, serif' },
  { value: 'visual-geez', label: 'ቪዥዋል ግዕዝ', family: 'Visual Geez Unicode, serif' }
];

const themes = [
  { value: 'light', label: 'ብሩህ', bg: '#ffffff', text: '#1f2937' },
  { value: 'dark', label: 'ጨለማ', bg: '#1f2937', text: '#f9fafb' },
  { value: 'sepia', label: 'ሴፒያ', bg: '#f7f3e9', text: '#5d4e37' },
  { value: 'high-contrast', label: 'ከፍተኛ ንፅፅር', bg: '#000000', text: '#ffffff' }
];

const getFontFamily = (fontValue) => {
  const font = fontFamilies.find(f => f.value === fontValue);
  return font ? font.family : 'system-ui, -apple-system, sans-serif';
};

const ReadingPreferences = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState({
    fontSize: 16,
    fontFamily: 'system',
    lineSpacing: 1.6,
    theme: 'sepia',
    readingMode: false
  });

  const applyPreferences = useCallback((prefs) => {
    const root = document.documentElement;
    root.style.setProperty('--app-font-size', `${prefs.fontSize}px`);
    root.style.setProperty('--app-line-height', prefs.lineSpacing);
    root.style.setProperty('--app-font-family', getFontFamily(prefs.fontFamily));

    // Apply theme via body class
    document.body.classList.remove('dark-theme', 'sepia-theme', 'high-contrast-theme');
    if (prefs.theme === 'dark') document.body.classList.add('dark-theme');
    else if (prefs.theme === 'sepia') document.body.classList.add('sepia-theme');
    else if (prefs.theme === 'high-contrast') document.body.classList.add('high-contrast-theme');

    document.body.classList.toggle('reading-mode', prefs.readingMode);

    // Also sync to fontPreferences key so Header picks it up on reload
    localStorage.setItem('fontPreferences', JSON.stringify({
      fontSize: prefs.fontSize,
      lineHeight: prefs.lineSpacing,
      fontFamily: prefs.fontFamily,
      theme: prefs.theme,
      readingMode: prefs.readingMode
    }));
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('gitsawe_reading_preferences') || localStorage.getItem('fontPreferences');
    if (saved) {
      const parsedPrefs = JSON.parse(saved);
      // Normalize lineSpacing/lineHeight field name
      if (parsedPrefs.lineHeight && !parsedPrefs.lineSpacing) parsedPrefs.lineSpacing = parsedPrefs.lineHeight;
      // Migrate old 'light' default to 'sepia'
      if (!parsedPrefs.theme || parsedPrefs.theme === 'light') parsedPrefs.theme = 'sepia';
      setPreferences(parsedPrefs);
      applyPreferences(parsedPrefs);
    } else {
      // Apply sepia default on first visit
      applyPreferences({ fontSize: 16, fontFamily: 'system', lineSpacing: 1.6, theme: 'sepia', readingMode: false });
    }
  }, [applyPreferences]);

  const updatePreference = (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    applyPreferences(newPrefs);
    
    // Save to localStorage
    localStorage.setItem('gitsawe_reading_preferences', JSON.stringify(newPrefs));
    
    // Show feedback
    toast.success('የንባብ ምርጫዎች ተቀምጠዋል!');
  };

  const resetPreferences = () => {
    const defaultPrefs = {
      fontSize: 16,
      fontFamily: 'system',
      lineSpacing: 1.6,
      theme: 'sepia',
      readingMode: false
    };
    
    setPreferences(defaultPrefs);
    applyPreferences(defaultPrefs);
    localStorage.setItem('gitsawe_reading_preferences', JSON.stringify(defaultPrefs));
    toast.success('የንባብ ምርጫዎች ወደ መጀመሪያው ተመልሰዋል!');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <span className="text-3xl">📖</span>
                የንባብ ምርጫዎች
              </h2>
              <p className="text-blue-100 mt-1">ለተሻለ የንባብ ተሞክሮ ያስተካክሉ</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Font Size */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-xl">🔤</span>
              የፊደል መጠን
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">ትንሽ</span>
                <span className="text-sm text-gray-600">ትልቅ</span>
              </div>
              <input
                type="range"
                min="5"
                max="28"
                value={preferences.fontSize}
                onChange={(e) => updatePreference('fontSize', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="text-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {preferences.fontSize}px
                </span>
              </div>
              <div 
                className="p-4 bg-gray-50 rounded-lg border"
                style={{ fontSize: `${preferences.fontSize}px` }}
              >
                ናሙና ጽሑፍ - የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን
              </div>
            </div>
          </div>

          {/* Font Family */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-xl">✍️</span>
              የፊደል አይነት
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fontFamilies.map((font) => (
                <button
                  key={font.value}
                  onClick={() => updatePreference('fontFamily', font.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                    preferences.fontFamily === font.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="font-medium">{font.label}</div>
                  <div 
                    className="text-sm text-gray-600 mt-1"
                    style={{ fontFamily: font.family }}
                  >
                    ናሙና ጽሑፍ ABC 123
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Line Spacing */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-xl">📏</span>
              የመስመር ክፍተት
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">ጠባብ</span>
                <span className="text-sm text-gray-600">ሰፊ</span>
              </div>
              <input
                type="range"
                min="1.2"
                max="2.5"
                step="0.1"
                value={preferences.lineSpacing}
                onChange={(e) => updatePreference('lineSpacing', parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="text-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {preferences.lineSpacing}x
                </span>
              </div>
              <div 
                className="p-4 bg-gray-50 rounded-lg border text-sm"
                style={{ lineHeight: preferences.lineSpacing }}
              >
                ይህ የመስመር ክፍተት ናሙና ነው። የኢትዮጵያ ኦርቶዶክስ ተዋሕዶ ቤተክርስቲያን የዲጂታል መጽሐፍት ቤት። የሊተርጂካል የቀን መቁጠሪያ፣ የቅዱሳን ዜመናት፣ የጸሎት ሰዓታት እና መፅሐፍ ቅዱሳዊ ምንጮች።
              </div>
            </div>
          </div>

          {/* Theme */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-xl">🎨</span>
              የቀለም ገጽታ
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {themes.map((theme) => (
                <button
                  key={theme.value}
                  onClick={() => updatePreference('theme', theme.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    preferences.theme === theme.value
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{ backgroundColor: theme.bg, color: theme.text }}
                >
                  <div className="font-medium text-sm">{theme.label}</div>
                  <div className="text-xs mt-1 opacity-75">ናሙና</div>
                </button>
              ))}
            </div>
          </div>

          {/* Reading Mode */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-xl">🔍</span>
              የንባብ ሁኔታ
            </h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">ትኩረት ያለው ንባብ</div>
                <div className="text-sm text-gray-600">አላስፈላጊ ነገሮችን ደብቆ ለንባብ ብቻ ያተኩራል</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.readingMode}
                  onChange={(e) => updatePreference('readingMode', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              onClick={resetPreferences}
              className="flex-1 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium"
            >
              ወደ መጀመሪያው መልስ
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-medium"
            >
              ተግባራዊ አድርግ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingPreferences;